import React, { useState } from 'react';
import { Box, Chip, Popover, Typography } from '@mui/material';
import { StatusStage } from './statusStages';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

/**
 * A status chip that opens a small list of every status the record type can move through, with the current one
 * marked and a one-line meaning for each. Extra props (e.g. data-help) go to the chip.
 */
const StatusStagesChip: React.FC<{
    label: string;
    status: string;
    stages: StatusStage[];
    color?: ChipColor;
    size?: 'small' | 'medium';
    sx?: object;
} & Record<string, unknown>> = ({ label, status, stages, color = 'default', size = 'small', sx, ...rest }) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    return (
        <>
            <Chip {...rest} label={label} color={color} size={size} sx={sx} aria-haspopup="dialog"
                onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)} />
            <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box role="dialog" aria-label="Status stages" sx={{ p: 1.5, maxWidth: 340 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Stages of this record</Typography>
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                        {stages.map(s => (
                            <Box component="li" key={s.value} data-stage={s.value} aria-current={s.value === status ? 'step' : undefined}
                                sx={{ py: 0.5, px: 0.75, borderRadius: 1, ...(s.value === status ? { bgcolor: 'action.selected' } : {}) }}>
                                <Typography variant="body2" sx={{ fontWeight: s.value === status ? 700 : 500 }}>
                                    {s.label}{s.value === status ? ' (current)' : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{s.meaning}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default StatusStagesChip;
